import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import {
  getTimelineData,
  getUpcomingWorkflowStageRequest,
} from 'src/redux/Actions/VehicleTracking';
import { useMediaQuery } from '@mui/system';
import { ReactComponent as ChangeStationIcon } from 'src/Images/icons/common/changeStationIcon.svg';
import UpcomingWorkflowStageRequestDrawer from 'src/components/VehicleTrackingDetails/VehicleTrackingTimeLine/UpcomingWorkflowStageRequestDrawer';
import ConfirmUpcomingWorkflowStageRequest from 'src/components/VehicleTrackingDetails/VehicleTrackingTimeLine/ConfirmUpcomingWorkflowStageRequest';
import { ReactComponent as ActivityTimelineIcon } from 'src/Images/icons/common/activityTimelineIcon.svg';

import { Drawer } from '@mui/material';

export default function VehicleTrackingTimeline({ workFlowStageId }) {
  const dispatch = useDispatch();
  const { vehicleTrackingNumber } = useParams();
  const isMobile = useMediaQuery('(max-width:600px)');
  const authReducer = useSelector((state) => state.AuthReducer);
  const themeColor = authReducer.userData.tenant?.theme;
  const organisationId = authReducer.userData.organisations?.id;
  const [openDrawer, setOpenDrawer] = useState(false);
  const [drawerStep, setDrawerStep] = useState('step1');
  const vehicleTrackingDataReducer = useSelector(
    (state) => state.VehicleTrackingReducer,
  );
  const { vehicleTrackingDetails, timeline: timelineData } =
    vehicleTrackingDataReducer;

  useEffect(() => {
    dispatch(getTimelineData(vehicleTrackingNumber, organisationId));
  }, [dispatch, vehicleTrackingNumber, organisationId]);

  useEffect(() => {
    if (vehicleTrackingDetails?.hasPendingRequestedWorkflowStage) {
      dispatch(
        getUpcomingWorkflowStageRequest(vehicleTrackingNumber, organisationId),
      );
    }
  }, [
    vehicleTrackingDetails?.hasPendingRequestedWorkflowStage,
    dispatch,
    vehicleTrackingNumber,
    organisationId,
  ]);

  const requestedStage = vehicleTrackingDetails?.requestedStage;

  const requestedStageExit = requestedStage?.exit_log_exist;
  const requestedStageEntry = requestedStage?.entry_log_exist;
  const canExitStage = !requestedStageExit && requestedStageEntry;

  const handleCloseDrawer = () => {
    setDrawerStep('step1');
    setOpenDrawer(false);
  };

  // Find the last index of the current workflow stage
  const lastCurrentStageIndex = timelineData?.timeline?.findLastIndex(
    (item) => item?.workflow_stage_id === Number(workFlowStageId),
  );

  return (
    <div className="timeline-container">
      <div className=" div-flex-row div-space-between div-align-center timeline-header-v2">
        <div className="d-flex justify-content-between align-items-center">
          <div className="text-md text-semi-bold">Activity Timeline</div>
          <div className="text-sm text-bold text-end"></div>
        </div>
        {canExitStage && (
          <div className="text-sm text-bold text-end">
            {isMobile ? (
              <ChangeStationIcon
                className="icon-size-lg cursor-pointer"
                onClick={() => setOpenDrawer(true)}
              />
            ) : (
              <ActivityTimelineIcon
                className="icon-size-lg cursor-pointer"
                onClick={() => setOpenDrawer(true)}
              />
            )}
          </div>
        )}
      </div>
      <ul
        className={`timeline-list-container theme-scrollable-container-${themeColor}`}
      >
        {timelineData?.timeline?.map((logData, index) => {
          let className = 'timeline-progress-v2';
          if (logData?.logs === null) {
            className = 'timeline-progress-v2';
          } else if (
            logData?.logs?.entry_timestamp &&
            logData?.logs?.exit_timestamp &&
            !logData?.skipped
          ) {
            className = 'timeline-progress-v2 is-complete';
          } else if (logData?.workflow_stage_id === Number(workFlowStageId)) {
            className = 'timeline-progress-v2 is-current';
          } else if (
            (logData?.logs?.entry_timestamp || logData?.logs?.exit_timestamp) &&
            !logData?.skipped
          ) {
            className = 'timeline-progress-v2 is-pending';
          } else if (logData?.skipped) {
            className = 'timeline-progress-v2 is-skipped';
          }

          // Check if this is the last occurrence of the current workflow stage
          const isLastCurrentStage =
            logData?.workflow_stage_id === Number(workFlowStageId) &&
            index === lastCurrentStageIndex;

          const entryTimestamp = logData?.logs?.entry_timestamp;
          const exitTimestamp = logData?.logs?.exit_timestamp;

          return (
            <li className={className} key={logData?.workflow_stage_id}>
              <div className="time-text-header">
                <div className="div-flex-row div-space-between div-align-center cg-10">
                  {/* Left: stage name + pills */}
                  <div className="div-flex-row cg-10 div-align-center">
                    <div className="div-flex-row time-text-header cg-10">
                      <div>
                        <span className="text-sm text-medium-bold">
                          {logData?.workflow_stage_name}
                        </span>
                      </div>
                      {isLastCurrentStage
                        ? !requestedStage?.completed &&
                          (canExitStage ? (
                            <div className="div-flex-row cg-5 div-align-center status-pill yellow">
                              <span className="text-xs">Awaiting Exit</span>
                            </div>
                          ) : (
                            <div className="div-flex-row cg-5 div-align-center status-pill blue">
                              <span className="text-xs">Awaiting Entry</span>
                            </div>
                          ))
                        : null}
                    </div>
                    {logData?.skipped && (
                      <div className="status-pill red">
                        <span className="text-xs">Skipped</span>
                      </div>
                    )}
                  </div>

                  {/* Right: entry | exit */}
                  {(entryTimestamp || exitTimestamp) && (
                    <div className="timeline-timestamps text-sm text-grey">
                      {entryTimestamp && <span>{entryTimestamp}</span>}
                      {entryTimestamp && exitTimestamp && (
                        <span className="timeline-timestamp-divider">|</span>
                      )}
                      {exitTimestamp && <span>{exitTimestamp}</span>}
                    </div>
                  )}
                </div>
              </div>
            </li>
          );
        })}
      </ul>
      <Drawer
        anchor="right"
        open={Boolean(openDrawer)}
        onClose={() => {
          setDrawerStep('step1');
          setOpenDrawer(false);
        }}
        ModalProps={{ keepMounted: false }}
        sx={{ zIndex: 1 }}
        PaperProps={{
          sx: { width: '100%' },
        }}
      >
        {vehicleTrackingDetails?.hasPendingRequestedWorkflowStage ? (
          <ConfirmUpcomingWorkflowStageRequest
            vehicleTrackingDetails={vehicleTrackingDetails}
            workFlowStageId={workFlowStageId}
            onClose={handleCloseDrawer}
            setDrawerStep={setDrawerStep}
          />
        ) : drawerStep === 'step1' ? (
          <UpcomingWorkflowStageRequestDrawer
            vehicleTrackingDetails={vehicleTrackingDetails}
            workFlowStageId={workFlowStageId}
            onClose={handleCloseDrawer}
            setDrawerStep={setDrawerStep}
          />
        ) : (
          <ConfirmUpcomingWorkflowStageRequest
            vehicleTrackingDetails={vehicleTrackingDetails}
            workFlowStageId={workFlowStageId}
            onClose={handleCloseDrawer}
            setDrawerStep={setDrawerStep}
          />
        )}
      </Drawer>
    </div>
  );
}
